import React, { useContext, useState, useRef } from 'react';
import styled from 'styled-components';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';
import {
  filterUseCasesByTag,
  getAllTags,
  StarterUseCase,
} from '@/features/editor/widgets/ai-widget-builder/use-cases/starter-use-cases';

// @ts-ignore
import AddImage from './assets/screenshot.png';
import { EditorContext } from '@/features/editor/context/editor-context';
import { DeviceType } from '@/utils/definitions';
import { HSpace, VSpace } from '@/components/spacing';
import { SearchInput } from '@/components/searchInput';
import { Flexbox } from '@/components/flex';

export const getStartersWithCards: AiConversationStarter = (
  sendMessage: (prompt: string, send: boolean) => void,
  attachFile: () => void,
  sketch: () => void,
): React.ReactNode => {
  const StarterWithCards = () => {
    const {
      devicePreview: {
        editorState: { device },
      },
    } = useContext(EditorContext);

    const [activeTag, setActiveTag] = useState<string>(`All`);
    const [searchQuery, setSearchQuery] = useState<string>(``);
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const tags = getAllTags();

    // Filter by tag first, then by search query
    let filteredUseCases = filterUseCasesByTag(activeTag);
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filteredUseCases = filteredUseCases.filter((useCase) => {
        const titleMatch = useCase.title.toLowerCase().includes(searchLower);
        const promptMatch = useCase.prompt?.toLowerCase().includes(searchLower);
        return titleMatch || promptMatch;
      });
    }

    const checkScrollAvailability = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };

    React.useEffect(() => {
      checkScrollAvailability();
      const scrollElement = scrollRef.current;
      if (scrollElement) {
        scrollElement.addEventListener(`scroll`, checkScrollAvailability);
        window.addEventListener(`resize`, checkScrollAvailability);
      }
      return () => {
        if (scrollElement) {
          scrollElement.removeEventListener(`scroll`, checkScrollAvailability);
        }
        window.removeEventListener(`resize`, checkScrollAvailability);
      };
    }, [tags]);

    const onClick = (useCase: StarterUseCase) => {
      if (useCase.openSketch) {
        sketch();
      }
      if (useCase.openImage) {
        attachFile();
      }
      if (useCase.prompt) {
        sendMessage(useCase.prompt, !!useCase.sendPrompt);
      }
    };

    const startScroll = (direction: 'left' | 'right') => {
      if (scrollIntervalRef.current) return;

      scrollIntervalRef.current = setInterval(() => {
        if (scrollRef.current) {
          const scrollAmount = direction === `left` ? -2 : 2;
          scrollRef.current.scrollLeft += scrollAmount;
        }
      }, 10);
    };

    const stopScroll = () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };

    return (
      <Container>
        <HeaderContainer>
          <Header>Starters</Header>
          <Flexbox direction="row" justify="center" align="center">
            <SearchInputWrapper>
              <SearchInput
                defaultValue=""
                value={searchQuery}
                grow
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchInputWrapper>
            <ArrowsContainer>
              <ScrollArrow
                direction="left"
                disabled={!canScrollLeft}
                onMouseEnter={() => startScroll(`left`)}
                onMouseLeave={stopScroll}
              >
                <MdChevronLeft />
              </ScrollArrow>
              <ScrollArrow
                direction="right"
                disabled={!canScrollRight}
                onMouseEnter={() => startScroll(`right`)}
                onMouseLeave={stopScroll}
              >
                <MdChevronRight />
              </ScrollArrow>
            </ArrowsContainer>
          </Flexbox>
        </HeaderContainer>

        <TabsContainer>
          <TabsScrollWrapper ref={scrollRef}>
            {tags.map((tag) => (
              <Tab
                key={tag}
                active={activeTag === tag}
                onClick={() => setActiveTag(tag)}
              >
                {tag}
              </Tab>
            ))}
            <HSpace value={3} />
          </TabsScrollWrapper>
        </TabsContainer>

        <CardsGrid
          className={device === DeviceType.Desktop ? `desktop` : `mobile`}
        >
          {filteredUseCases.map((useCase, index) => (
            <Card key={index} onClick={() => onClick(useCase)}>
              <CardImage
                className={device === DeviceType.Desktop ? `desktop` : `mobile`}
                src={useCase.image || AddImage}
                alt={useCase.title}
              />
              <CardTitle>{useCase.title}</CardTitle>
            </Card>
          ))}
        </CardsGrid>
        <VSpace value={20} />
      </Container>
    );
  };
  return <StarterWithCards />;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-x: visible;
  overflow-y: scroll;
  gap: 2rem;
  padding-top: 2rem;
  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    rgb(248 249 251 / 1) 0%,
    rgb(248 249 251 / 1) 85%,
    hsl(0 0% 0% / 0)
  );
`;

const Header = styled.h2`
  font-family: 'Eesti', serif;
  font-size: 2.4rem;
  font-weight: 600;
  color: #000;
  margin: 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 1rem;
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  max-width: 300px;
  margin: 0 1rem;
`;

const ArrowsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const TabsContainer = styled.div`
  position: relative;
`;

const ScrollArrow = styled.button<{ direction: string; disabled?: boolean }>`
  flex-shrink: 0;
  border: none;
  background-color: transparent;
  color: ${(props) => (props.disabled ? `#e0e0e0` : `#000`)};
  padding: 0;
  font-size: 2.8rem;
  line-height: 1;
  cursor: ${(props) => (props.disabled ? `default` : `pointer`)};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  z-index: 1;
  user-select: none;
  pointer-events: ${(props) => (props.disabled ? `none` : `auto`)};

  &:hover {
    color: ${(props) => (props.disabled ? `#e0e0e0` : `#333`)};
  }

  &:active {
    transform: ${(props) => (props.disabled ? `none` : `scale(0.95)`)};
  }
`;

const TabsScrollWrapper = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  mask-image: linear-gradient(
    var(--mask-direction, to right),
    hsl(0 0% 0% / 0),
    rgb(248 249 251 / 1) 1%,
    rgb(248 249 251 / 1) 95%,
    hsl(0 0% 0% / 0)
  );

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  flex-shrink: 0;
  padding: 1rem 2.4rem;
  border: none;
  border-radius: 3rem;
  background-color: ${(props) => (props.active ? `#000` : `#e7ebee`)};
  color: ${(props) => (props.active ? `#fff` : `#666`)};
  font-size: 1.4rem;
  font-weight: 500;
  font-family: 'Eesti', serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => (props.active ? `#000` : `#e8e8e8`)};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  padding: 1rem;

  &&.desktop {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer !important;
  transition: all 0.25s ease;
  overflow: visible;

  &:hover {
    transform: translateY(-4px);
    cursor: pointer !important;
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  border-radius: 1.5rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);

  &&.desktop {
    border-radius: 1rem;
  }

  &:hover {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.55);
  }
  transition: box-shadow 0.25s ease;
`;

const CardTitle = styled.h3`
  font-family: 'Eesti', serif;
  font-size: 1.3rem;
  font-weight: 400;
  padding: 0 1rem;
  color: #000;
  line-height: 1.4;
  text-align: left;
`;
